import { endSession, IDLE_TIMEOUT_REASON } from './session'

/**
 * Client side of the SSO idle timeout. The server is the control: it records activity and refuses
 * a session that has gone idle whatever this module does. This module exists so the user is signed
 * out the moment the timeout passes rather than on their next request, and so the server can be
 * told how long the user has actually been idle.
 *
 * The user's last interaction, in any tab, is held in localStorage so every tab reads the same
 * value; that shared value is what keeps every tab alive while the user works in any one of them.
 * The end of the session is announced over BroadcastChannel, falling back to the storage event,
 * so every tab returns to sign-in together.
 */

/**
 * The interactions that count as the user being present, together with route changes (see
 * `watchActivity`). Nothing else ever records activity — not timers, polling, Firestore listeners
 * or token refresh.
 *
 * Listened for in the capture phase, so scrolling inside a table or panel counts as well as
 * scrolling the page. Note the scroll event also fires when a page scrolls itself, so a view must
 * not scroll programmatically on a timer or on live updates, or it will hold the session open.
 */
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'wheel', 'touchstart', 'touchmove']

const LAST_ACTIVITY_KEY = 'lastActivityAt'

const EXPIRED_KEY = 'idleSessionExpiredAt'

const CHANNEL_NAME = 'nuport-idle-session'

/** Interactions closer together than this are the same activity, so scrolling and typing do not write on every event. */
const ACTIVITY_RESOLUTION_MS = 1000

/**
 * Interaction that made no request of its own is reported at most this often. The first
 * interaction after a quiet spell is reported at once, so the server always hears of it before its
 * window closes, and each report carries the exact time of the latest interaction.
 */
const REPORT_INTERVAL_MS = 60 * 1000

const CHECK_INTERVAL_MS = 5 * 1000

/** How long the server gets to agree the session has expired before this device signs out regardless. */
const EXPIRE_REQUEST_TIMEOUT_MS = 3000

/**
 * A user-initiated operation holds the session open while it runs, but not indefinitely: a request
 * that never settles must not switch the timeout off.
 */
const MAX_OPERATION_MS = 2 * 60 * 60 * 1000

let timeoutMs = null
let reportActivity = null
let expireSession = null
let checkTimer = null
let reportTimer = null
let lastReportedAt = 0
let channel = null
let watching = false
let nextOperationId = 0
const operations = new Map()

/** Used only where localStorage is unavailable, so this tab at least still tracks itself. */
let lastActivityInMemory = null

const readLastActivityAt = () => {
	try {
		const value = Number(window.localStorage.getItem(LAST_ACTIVITY_KEY))
		return value > 0 ? value : null
	} catch (e) {
		return lastActivityInMemory
	}
}

const writeLastActivityAt = at => {
	lastActivityInMemory = at
	try {
		window.localStorage.setItem(LAST_ACTIVITY_KEY, String(at))
	} catch (e) {
		// Storage unavailable. The in-memory value still covers this tab.
	}
}

const hasOperationInFlight = now => {
	for (const startedAt of operations.values()) {
		if (now - startedAt < MAX_OPERATION_MS) return true
	}
	return false
}

/**
 * Milliseconds since the user last interacted, for the header every API request carries, or null
 * when nothing has been recorded yet. Zero while a user-initiated operation is in flight.
 */
export const getIdleMs = () => {
	if (typeof window === 'undefined') return null
	const now = Date.now()
	if (hasOperationInFlight(now)) return 0
	const lastActivityAt = readLastActivityAt()
	return lastActivityAt === null ? null : Math.max(0, now - lastActivityAt)
}

const scheduleReport = () => {
	if (!timeoutMs || reportTimer) return
	const wait = Math.max(0, lastReportedAt + REPORT_INTERVAL_MS - Date.now())
	reportTimer = setTimeout(() => {
		reportTimer = null
		lastReportedAt = Date.now()
		// A report refused because the session has ended is handled by the API client; anything
		// else is only a missed heartbeat, and the next interaction sends another.
		Promise.resolve(reportActivity?.()).catch(() => {})
	}, wait)
}

export const recordActivity = () => {
	const now = Date.now()
	const lastActivityAt = readLastActivityAt()
	if (lastActivityAt !== null && now - lastActivityAt < ACTIVITY_RESOLUTION_MS) return
	writeLastActivityAt(now)
	scheduleReport()
}

/** Starts the window afresh, as the server does when it creates a session. */
export const resetActivity = () => {
	writeLastActivityAt(Date.now())
}

/**
 * Listens for genuine interaction. Installed once, for the life of the page, and before any
 * session exists, so the interaction that signs the user in already counts.
 */
export const watchActivity = routerEvents => {
	if (watching || typeof window === 'undefined') return
	watching = true
	ACTIVITY_EVENTS.forEach(type => {
		window.addEventListener(type, recordActivity, { capture: true, passive: true })
	})
	routerEvents.on('routeChangeComplete', recordActivity)
	// Timers in a background tab are throttled, so look again as soon as the tab is back in view.
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'visible') check()
	})
}

/** Marks a user-initiated operation, such as an upload or an export, as in progress. */
export const beginOperation = () => {
	const id = ++nextOperationId
	operations.set(id, Date.now())
	return id
}

export const endOperation = id => {
	if (operations.delete(id)) recordActivity()
}

const check = () => {
	if (!timeoutMs) return
	const now = Date.now()
	if (hasOperationInFlight(now)) {
		recordActivity()
		return
	}
	const lastActivityAt = readLastActivityAt()
	if (lastActivityAt === null || now - lastActivityAt < timeoutMs) return
	expire()
}

const withTimeout = (promise, ms) => Promise.race([
	Promise.resolve(promise).catch(() => {}),
	new Promise(resolve => setTimeout(resolve, ms))
])

/**
 * The timeout passed in this tab. The server is asked to end the session first — which revokes the
 * refresh token — while this device still holds credentials to ask with, and only then are the
 * other tabs told.
 */
const expire = async () => {
	const expireOnServer = expireSession
	stopIdleSession()
	await withTimeout(expireOnServer?.(), EXPIRE_REQUEST_TIMEOUT_MS)
	endIdleSession()
}

/** Ends the session in every tab and returns to sign-in with the inactivity message. */
export const endIdleSession = () => {
	stopIdleSession()
	if (typeof BroadcastChannel !== 'undefined') {
		try {
			const announcement = new BroadcastChannel(CHANNEL_NAME)
			announcement.postMessage({ type: 'expired' })
			announcement.close()
		} catch (e) {
			// Other tabs find out on their next request instead.
		}
	} else {
		try {
			window.localStorage.setItem(EXPIRED_KEY, String(Date.now()))
		} catch (e) {
			// Other tabs find out on their next request instead.
		}
	}
	endSession(IDLE_TIMEOUT_REASON)
}

const onExpiredElsewhere = () => {
	if (!timeoutMs) return
	stopIdleSession()
	endSession(IDLE_TIMEOUT_REASON)
}

const onChannelMessage = event => {
	if (event.data?.type === 'expired') onExpiredElsewhere()
}

const onStorage = event => {
	if (event.key === EXPIRED_KEY && event.newValue) onExpiredElsewhere()
}

/**
 * Starts enforcing the timeout the server set for this session. A session without one (null) is
 * not subject to the idle timeout, and nothing runs.
 */
export const startIdleSession = ({ timeoutMs: sessionTimeoutMs, onReport, onExpire }) => {
	stopIdleSession()
	if (!sessionTimeoutMs || typeof window === 'undefined') return
	timeoutMs = sessionTimeoutMs
	reportActivity = onReport
	expireSession = onExpire
	if (readLastActivityAt() === null) resetActivity()
	if (typeof BroadcastChannel !== 'undefined') {
		channel = new BroadcastChannel(CHANNEL_NAME)
		channel.onmessage = onChannelMessage
	} else {
		window.addEventListener('storage', onStorage)
	}
	checkTimer = setInterval(check, CHECK_INTERVAL_MS)
	check()
}

export const stopIdleSession = () => {
	timeoutMs = null
	reportActivity = null
	expireSession = null
	clearInterval(checkTimer)
	checkTimer = null
	clearTimeout(reportTimer)
	reportTimer = null
	if (channel) {
		channel.close()
		channel = null
	}
	if (typeof window !== 'undefined') {
		window.removeEventListener('storage', onStorage)
	}
}
